/** Platform-neutral semantic tokens shared by web and future native UI layers. */
export const lightColors = {
  background: '#f5f7fa',
  surface: '#ffffff',
  surfaceElevated: '#ffffff',
  sidebar: '#18191b',
  sidebarMuted: '#a7abb2',
  textPrimary: '#17191c',
  textSecondary: '#5f6670',
  border: '#e2e6eb',
  primary: '#0875d6',
  primaryHover: '#0668c4',
  success: '#177a4b',
  successSurface: '#e8f7ef',
  warning: '#9a5b00',
  warningSurface: '#fff4df',
  danger: '#b42318',
  dangerSurface: '#feeceb',
  info: '#0868a9',
  infoSurface: '#e6f3ff',
  processing: '#7a3e9d',
  processingSurface: '#f5eafb',
  focusRing: '#0875d6',
} as const;

export const darkColors = {
  background: '#111317',
  surface: '#1a1d22',
  surfaceElevated: '#22262d',
  sidebar: '#0d0f12',
  sidebarMuted: '#aeb4be',
  textPrimary: '#f4f6f8',
  textSecondary: '#b6bdc8',
  border: '#343a44',
  primary: '#4da3f5',
  primaryHover: '#79baff',
  success: '#72d3a2',
  successSurface: '#163b2a',
  warning: '#f1bd67',
  warningSurface: '#483416',
  danger: '#ff958c',
  dangerSurface: '#4b211f',
  info: '#74b9f5',
  infoSurface: '#173650',
  processing: '#d3a1ee',
  processingSurface: '#3e2949',
  focusRing: '#79baff',
} as const;

/** Existing React Native light-theme aliases; retained until mobile adopts theme selection. */
export const colors = {
  primary: lightColors.info,
  primaryDark: '#07558c',
  primaryLight: lightColors.infoSurface,
  background: lightColors.background,
  surface: lightColors.surface,
  sidebar: '#f8f8f8',
  border: lightColors.border,
  text: lightColors.textPrimary,
  textMuted: lightColors.textSecondary,
  success: lightColors.success,
  successLight: lightColors.successSurface,
  warning: lightColors.warning,
  warningLight: lightColors.warningSurface,
  critical: lightColors.danger,
  criticalLight: lightColors.dangerSurface,
} as const;

export const spacing = { xs: 4, sm: 8, md: 16, lg: 24, xl: 32 } as const;
export const radii = { sm: 8, md: 10, lg: 12, pill: 999 } as const;
export const typography = { small: 12, body: 14, title: 24, metric: 30 } as const;
export const shadows = {
  card: {
    shadowColor: '#0b2239',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 2,
  },
  lightCss: '0 2px 12px rgb(15 23 42 / 7%)',
  darkCss: '0 2px 12px rgb(0 0 0 / 28%)',
} as const;

export const tokens = { colors, lightColors, darkColors, spacing, radii, typography, shadows } as const;
export type ThemeTokens = typeof tokens;
